import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SeoTags } from "../../../components/Head/SeoTags";
import { McpMock } from "../../../components/Mockups/McpMock";

const MCP_ENDPOINT = `${process.env.REACT_APP_API_URL}/mcp`;

const Mcp = () => {
  const { t } = useTranslation();

  return (
    <>
      <SeoTags
        title={t("featurePages.mcp.seo.title")}
        description={t("featurePages.mcp.seo.description")}
        keywords={t("featurePages.mcp.seo.keywords")}
        localized={false}
      />

      {/* Structured Data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: t("featurePages.mcp.seo.title"),
            description: t("featurePages.mcp.seo.description"),
            url: "https://onbookmarks.com/features/mcp/",
            mainEntity: {
              "@type": "SoftwareApplication",
              name: "onBookmarks",
              applicationCategory: "ProductivityApplication",
              operatingSystem: "Web, iOS, Android",
              featureList: [
                "Model Context Protocol (MCP) endpoint",
                "Connect Claude, ChatGPT and any MCP client",
                "AI agents can read and search bookmarks",
                "AI agents can create, tag and organize bookmarks",
                "Scoped, revocable personal access tokens",
              ],
              offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
                description: "Free plan includes MCP access",
              },
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://onbookmarks.com",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Features",
                  item: "https://onbookmarks.com/features/",
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: "MCP",
                  item: "https://onbookmarks.com/features/mcp/",
                },
              ],
            },
          }),
        }}
      />

      {/* Hero */}
      <section className="hero bg-purple-subtle">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-6">
              <div className="d-flex flex-column gap-4">
                <div>
                  <span className="badge bg-purple text-white px-3 py-2">
                    {t("featurePages.mcp.hero.badge")}
                  </span>
                </div>
                <h1 className="display-4 fw-bold">
                  {t("featurePages.mcp.hero.title")}
                </h1>
                <p className="lead">
                  {t("featurePages.mcp.hero.description")}
                </p>
                <div className="d-flex flex-wrap align-items-center gap-3">
                  <Link
                    to={`${process.env.REACT_APP_APP_URL}/register`}
                    className="btn btn-primary btn-lg px-4"
                  >
                    {t("featurePages.mcp.hero.cta")}
                  </Link>
                  <code className="bg-white border rounded px-3 py-2 small text-break">
                    {MCP_ENDPOINT}
                  </code>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <McpMock />
            </div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center mb-5">
              <h2 className="h1 fw-bold mb-4">
                {t("featurePages.mcp.problem.title")}
              </h2>
              <p className="lead text-muted">
                {t("featurePages.mcp.problem.description")}
              </p>
            </div>
          </div>

          <div className="row g-4 mb-2">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-wind fs-2 text-danger mb-3"></i>
                  <h3 className="h5 mb-3">
                    {t("featurePages.mcp.problem.lost.title")}
                  </h3>
                  <p className="text-muted mb-0">
                    {t("featurePages.mcp.problem.lost.description")}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-clipboard2-x fs-2 text-danger mb-3"></i>
                  <h3 className="h5 mb-3">
                    {t("featurePages.mcp.problem.manual.title")}
                  </h3>
                  <p className="text-muted mb-0">
                    {t("featurePages.mcp.problem.manual.description")}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-eye-slash fs-2 text-danger mb-3"></i>
                  <h3 className="h5 mb-3">
                    {t("featurePages.mcp.problem.blind.title")}
                  </h3>
                  <p className="text-muted mb-0">
                    {t("featurePages.mcp.problem.blind.description")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <div className="mb-3">
                <span className="badge bg-purple text-white px-3 py-2">
                  {t("featurePages.mcp.solution.badge")}
                </span>
              </div>
              <McpMock />
            </div>
            <div className="col-lg-6">
              <h2 className="h1 fw-bold mb-4">
                {t("featurePages.mcp.solution.title")}
              </h2>
              <p className="lead mb-4">
                {t("featurePages.mcp.solution.description")}
              </p>
              <ul className="list-unstyled">
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.mcp.solution.benefit1.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("featurePages.mcp.solution.benefit1.description")}
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.mcp.solution.benefit2.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("featurePages.mcp.solution.benefit2.description")}
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.mcp.solution.benefit3.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("featurePages.mcp.solution.benefit3.description")}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="h1 fw-bold mb-4">
              {t("featurePages.mcp.howItWorks.title")}
            </h2>
            <p className="lead text-muted">
              {t("featurePages.mcp.howItWorks.description")}
            </p>
          </div>

          <div className="row g-4">
            {["step1", "step2", "step3"].map((step, idx) => (
              <div className="col-md-4" key={step}>
                <div className="text-center">
                  <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                    <span className="fs-2 fw-bold text-purple">{idx + 1}</span>
                  </div>
                  <h3 className="h5 mb-3">
                    {t(`featurePages.mcp.howItWorks.${step}.title`)}
                  </h3>
                  <p className="text-muted">
                    {t(`featurePages.mcp.howItWorks.${step}.description`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Toolset */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center mb-5">
              <h2 className="h1 fw-bold mb-4">
                {t("featurePages.mcp.tools.title")}
              </h2>
              <p className="lead text-muted">
                {t("featurePages.mcp.tools.description")}
              </p>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-search fs-3 text-purple me-3"></i>
                    <h3 className="h5 mb-0">
                      {t("featurePages.mcp.tools.read.title")}
                    </h3>
                  </div>
                  <ul className="list-unstyled mb-0">
                    {["item1", "item2", "item3", "item4"].map((it) => (
                      <li className="mb-2" key={it}>
                        <i className="bi bi-check-lg text-success me-2"></i>
                        {t(`featurePages.mcp.tools.read.${it}`)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-pencil-square fs-3 text-purple me-3"></i>
                    <h3 className="h5 mb-0">
                      {t("featurePages.mcp.tools.write.title")}
                    </h3>
                  </div>
                  <ul className="list-unstyled mb-0">
                    {["item1", "item2", "item3", "item4"].map((it) => (
                      <li className="mb-2" key={it}>
                        <i className="bi bi-check-lg text-success me-2"></i>
                        {t(`featurePages.mcp.tools.write.${it}`)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center mb-5">
              <h2 className="h1 fw-bold mb-4">
                {t("featurePages.mcp.useCases.title")}
              </h2>
              <p className="lead text-muted">
                {t("featurePages.mcp.useCases.description")}
              </p>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-robot fs-3 text-purple me-3"></i>
                    <h3 className="h5 mb-0">
                      {t("featurePages.mcp.useCases.research.title")}
                    </h3>
                  </div>
                  <p className="text-muted mb-3">
                    {t("featurePages.mcp.useCases.research.description")}
                  </p>
                  <ul className="list-unstyled mb-0">
                    {["example1", "example2", "example3"].map((ex) => (
                      <li className="mb-2" key={ex}>
                        <i className="bi bi-arrow-right text-purple me-2"></i>
                        {t(`featurePages.mcp.useCases.research.${ex}`)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-lightning-charge fs-3 text-purple me-3"></i>
                    <h3 className="h5 mb-0">
                      {t("featurePages.mcp.useCases.workflow.title")}
                    </h3>
                  </div>
                  <p className="text-muted mb-3">
                    {t("featurePages.mcp.useCases.workflow.description")}
                  </p>
                  <ul className="list-unstyled mb-0">
                    {["example1", "example2", "example3"].map((ex) => (
                      <li className="mb-2" key={ex}>
                        <i className="bi bi-arrow-right text-purple me-2"></i>
                        {t(`featurePages.mcp.useCases.workflow.${ex}`)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy / control callout */}
      <section className="py-4">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="card border-0 bg-light">
                <div className="card-body p-4 d-flex align-items-start gap-3">
                  <i className="bi bi-shield-check fs-3 text-success"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.mcp.privacy.title")}
                    </h4>
                    <p className="text-muted mb-0 small">
                      {t("featurePages.mcp.privacy.description")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-5">
        <div className="container">
          <div className="bg-purple rounded-4 p-5 text-center text-white">
            <h2 className="h1 fw-bold mb-4">
              {t("featurePages.mcp.cta.title")}
            </h2>
            <p className="lead mb-4">
              {t("featurePages.mcp.cta.description")}
            </p>
            <Link
              to={`${process.env.REACT_APP_APP_URL}/register`}
              className="btn btn-light btn-lg px-5"
            >
              {t("featurePages.mcp.cta.button")}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Mcp;
